import * as React from "react";
import { ArrowRightIcon } from "lucide-react";
import { PopoverClose } from "@radix-ui/react-popover";
import { Slot, Slottable } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * A small "are you sure?" over its trigger (`children`, rendered asChild):
 * a title, an optional `action` beside it (a ConfirmPopoverLink to where the
 * copy is edited), the consequences, then Cancel and the confirm button. For
 * a step that needs typing or reading more, use an AlertDialog instead.
 *
 * @example
 * import { RotateCcw } from "lucide-react"
 * import { Button } from "@/components/ui/button"
 * import { ConfirmPopover } from "@/components/ui/confirm-popover"
 *
 * export function ResetTemplate({ onReset }: { onReset: () => void }) {
 *   return (
 *     <ConfirmPopover
 *       title="Reset the template?"
 *       description="It goes back to the default text. You can edit it again any time."
 *       confirmLabel={<><RotateCcw />Reset</>}
 *       onConfirm={onReset}
 *     >
 *       <Button variant="ghost" size="sm">Reset</Button>
 *     </ConfirmPopover>
 *   )
 * }
 */
export function ConfirmPopover({
  children,
  title,
  description,
  action,
  confirmLabel,
  confirmVariant = "default",
  onConfirm,
  disabled,
}: {
  children: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  /** Beside the title — usually a ConfirmPopoverLink. */
  action?: React.ReactNode;
  /** The confirm button's content — icon and text. */
  confirmLabel: React.ReactNode;
  confirmVariant?: VariantProps<typeof buttonVariants>["variant"];
  onConfirm: () => void;
  disabled?: boolean;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <div className="mb-1 flex items-center justify-between gap-2 text-sm font-medium">
          {title}
          {action}
        </div>
        <div className="mb-3 flex flex-col items-start gap-2 text-xs text-muted-foreground">
          {description}
        </div>
        <div className="flex justify-end gap-2">
          <PopoverClose asChild>
            <Button variant="secondary" size="sm" disabled={disabled}>
              Cancel
            </Button>
          </PopoverClose>
          <PopoverClose asChild>
            <Button variant={confirmVariant} size="sm" onClick={onConfirm} disabled={disabled}>
              {confirmLabel}
            </Button>
          </PopoverClose>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The "Edit →" link beside a ConfirmPopover title. Pass your router's link
 * with `asChild`: `<ConfirmPopoverLink asChild><Link to="…">Edit</Link></ConfirmPopoverLink>`.
 */
export function ConfirmPopoverLink({
  asChild = false,
  className,
  children,
  ...props
}: React.ComponentProps<"a"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "a";
  return (
    <Comp
      className={cn(
        "group flex items-center text-xs font-normal text-muted-foreground underline underline-offset-2 hover:text-foreground",
        className,
      )}
      {...props}
    >
      <Slottable>{children}</Slottable>
      <ArrowRightIcon className="ml-0.5 size-3.5 group-hover:translate-x-0.5" />
    </Comp>
  );
}
